import { DialogRef } from '@angular/cdk/dialog';

import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	Inject,
	ViewChild,
	WritableSignal,
	forwardRef,
	inject,
	signal,
	AfterViewInit,
} from '@angular/core';
import {
	FormBuilder,
	FormControl,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { CmmsLabelsSectionComponent } from '@app/pages/cmms/components/shared-form-group-controls/cmms-labels-section/cmms-labels-section.component';
import { CmmsLocationsSectionComponent } from '@app/pages/cmms/components/shared-form-group-controls/cmms-locations-section/cmms-locations-section.component';
import { Store } from '@ngrx/store';
import { ContactsService } from '@app/pages/contacts/services/contacts.service';
import { AuUtilsFunctions } from '@app/shared';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';

@Component({
	selector: 'add-contact',
	templateUrl: './add-contact.component.html',
	styleUrls: ['./add-contact.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatFormFieldModule,
		MatInputModule,
		MatFormFieldModule,
		ReactiveFormsModule,
		MatIconModule,
		forwardRef(() => CmmsLocationsSectionComponent),
		CmmsLabelsSectionComponent,
		MatButtonModule,
		NgxTolgeeModule,
	],
})
export class AddContactComponent implements AfterViewInit {
	@ViewChild('recipientsInput') recipientsInput: ElementRef;
	private readonly fb = inject(FormBuilder);
	private readonly dialogRef = inject(DialogRef<any>);
	readonly store$ = inject(Store);
	private readonly notify = inject(IconSnackBarService);
	private readonly contact = inject(ContactsService);
	private readonly translateService = inject(TranslateService);

	initialLabel;
	addContactForm = this.fb.group({
		firstName: [''],
		lastName: [''],
		company: [''],
		email: [
			'',
			[Validators.pattern(/^(?!.*[æøå])[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)],
		],
		countryCode: ['+47'],
		phoneNumber: ['', [Validators.pattern(/^\d{8}$/)]],
		location: [],
		labels: [],
	});
	editContact: WritableSignal<boolean> = signal(false);
	isDirty: WritableSignal<boolean> = signal(false);

	constructor(
		@Inject(MAT_DIALOG_DATA)
		private data
	) {
		if (this.data?.initialData) {
			Object.entries(this.data.initialData).forEach(([key, value]) => {
				if (value) {
					switch (key) {
						case 'labels':
							this.addContactForm.controls[key]?.setValue(value);
							this.initialLabel = value;
							break;
						case 'location':
							this.addContactForm.controls.location.setValue([value]);
							break;
						default:
							this.addContactForm.controls[key]?.setValue(value);
					}
				}
			});
		}

		this.addContactForm.valueChanges.subscribe(_ => {
			this.editContact.set(this.data.editContact);
			if (!this.editContact()) {
				const copyObject = { ...this.addContactForm.value };
				delete copyObject.location;
				delete copyObject.labels;
				delete copyObject.countryCode;

				const isFormEmpty = Object.keys(copyObject).every(key => !copyObject[key]);
				this.isDirty.set(!isFormEmpty);
			}

			if (this.editContact()) {
				const editData = { ...this.data.initialData };

				if (!editData.location) {
					editData.location = [this.data.initialData.location];
				}

				Object.keys(editData).forEach(key => {
					if (!(key in this.addContactForm.value)) {
						delete editData[key];
					}
				});
				const diff = AuUtilsFunctions.diff(editData, this.addContactForm.value);
				this.isDirty.set(AuUtilsFunctions.objectNotEmpty(diff));
			}
		});
	}

	handleClick() {
		if (this.editContact()) {
			this.onEditContact();
		} else {
			this.onAddContact();
		}
	}

	onAddContact() {
		this.contact.createContact(this.addContactForm.value).subscribe(_ => {
			this.dialogRef.close();
			this.notify.success(this.translateService.instant('contact_created_success'));
		});
	}

	onEditContact() {
		const locations = this.addContactForm.value.location;
		const removeLocation =
			(!locations || locations.length === 0) && this.data.initialData.location
				? true
				: false;

		this.contact
			.editContact(this.addContactForm.value, this.data.initialData.pk, removeLocation)
			.subscribe(_ => {
				this.contact.updateContact(this.addContactForm.value, this.data.initialData.pk);
				this.dialogRef.close();
				this.notify.success(this.translateService.instant('contact_updated_success'));
			});
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.recipientsInput.nativeElement.focus();
		}, 300);
	}

	onCloseDialog() {
		this.dialogRef.close();
	}

	updateFormData(name: string, control: FormControl) {
		this.addContactForm.controls[name].setValue(control);
	}

	invalidEmail() {
		const email = this.addContactForm.controls.email.value;
		if (email && !email.includes('@')) {
			return 'remember_to_add_at';
		}
		if (email.includes('æ') || email.includes('ø') || email.includes('å')) {
			return 'remember_use_valid_chars';
		}
		return '';
	}

	invalidPhonenumber() {
		const phonenumber = this.addContactForm.controls.phoneNumber.value;
		if (!phonenumber.match(/^\d+$/)) {
			return 'phone_number_only_digits';
		}
		//Norwegian standard for phone numbers. Should be made dynamic when adding support for other countries.
		if (phonenumber.length < 8 || phonenumber.length > 8) {
			return 'phone_number_eight_digits';
		}
		return '';
	}
}
